import { useEffect, useMemo, useState } from 'react';
import type { CcyCode, RateProviderInfo, RateSourceConfig } from '@wave/shared';
import { Modal } from '../Modal';
import { useRateProviders, useRateSources } from '../../api/hooks';
import { useUpdateRateSources } from '../../api/mutations';
import { btnGhostSm, btnPrimary, btnSecondary, btnSuccessSm } from '../../lib/styles';

/**
 * Редактор цепочки источников курса для одной валюты. Позволяет добавить
 * несколько провайдеров, задать им порядок (priority 0 = primary), переключить
 * enabled, и заполнить params (если у провайдера есть paramsSchema).
 *
 * Сохранение — атомарная замена цепочки через PUT /ref/usdt-rates/:ccy/sources.
 */
export function RateSourcesModal({ ccy, onClose }: { ccy: CcyCode; onClose: () => void }) {
  const { data: providers } = useRateProviders();
  const { data: existing } = useRateSources(ccy);
  const update = useUpdateRateSources(ccy);
  const [draft, setDraft] = useState<RateSourceConfig[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing) setDraft(existing);
  }, [existing]);

  const availableProviders = useMemo(
    () => (providers ?? []).filter((p) => p.supportsAnyCcy || p.supportedCcy.includes(ccy)),
    [providers, ccy],
  );

  function addSource() {
    if (availableProviders.length === 0) return;
    const nextPriority = draft.length === 0 ? 0 : Math.max(...draft.map((d) => d.priority)) + 1;
    setDraft([
      ...draft,
      { priority: nextPriority, providerKey: availableProviders[0]!.key, params: {}, enabled: true },
    ]);
  }

  function removeSource(idx: number) {
    setDraft(draft.filter((_, i) => i !== idx).map((s, i) => ({ ...s, priority: i })));
  }

  function move(idx: number, direction: -1 | 1) {
    const target = idx + direction;
    if (target < 0 || target >= draft.length) return;
    const next = [...draft];
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    setDraft(next.map((s, i) => ({ ...s, priority: i })));
  }

  function updateSource(idx: number, patch: Partial<RateSourceConfig>) {
    setDraft(draft.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  }

  async function save() {
    setError(null);
    try {
      await update.mutateAsync(draft);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <Modal title={`Источники курса · ${ccy}`} onClose={onClose} size="lg">
      <div className="space-y-4">
        <p className="text-sm text-text2">
          Provider с приоритетом <strong>0</strong> — primary; следующие — fallback при ошибке.
          Провайдер «manual» не имеет смысла в цепочке (он не делает HTTP-запросов) — для ручного ввода
          используйте поле курса напрямую.
        </p>

        {draft.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-bg2 px-4 py-6 text-center text-sm text-text2">
            Цепочка пуста. Курс можно обновлять только вручную.
          </div>
        ) : (
          <div className="space-y-2">
            {draft.map((src, idx) => (
              <SourceRow
                key={idx}
                index={idx}
                isFirst={idx === 0}
                isLast={idx === draft.length - 1}
                config={src}
                providers={availableProviders}
                onUpdate={(patch) => updateSource(idx, patch)}
                onRemove={() => removeSource(idx)}
                onMoveUp={() => move(idx, -1)}
                onMoveDown={() => move(idx, 1)}
              />
            ))}
          </div>
        )}

        <button type="button" onClick={addSource} className={btnGhostSm}>
          + Добавить источник
        </button>

        {error && (
          <div className="rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-border-subtle pt-4">
          <button type="button" onClick={onClose} className={btnSecondary}>
            Отмена
          </button>
          <button
            type="button"
            disabled={update.isPending}
            onClick={() => void save()}
            className={btnPrimary}
          >
            {update.isPending ? 'Сохранение…' : 'Сохранить цепочку'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SourceRow({
  index,
  isFirst,
  isLast,
  config,
  providers,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  index: number;
  isFirst: boolean;
  isLast: boolean;
  config: RateSourceConfig;
  providers: RateProviderInfo[];
  onUpdate: (patch: Partial<RateSourceConfig>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const provider = providers.find((p) => p.key === config.providerKey);
  const hasParams = !!provider?.paramsSchema;
  const paramsJson = useMemo(() => JSON.stringify(config.params, null, 2), [config.params]);
  const [paramsDraft, setParamsDraft] = useState(paramsJson);
  const [paramsError, setParamsError] = useState<string | null>(null);

  useEffect(() => {
    setParamsDraft(paramsJson);
    setParamsError(null);
  }, [paramsJson]);

  function commitParams() {
    try {
      const parsed = paramsDraft.trim() === '' ? {} : JSON.parse(paramsDraft);
      onUpdate({ params: parsed });
      setParamsError(null);
    } catch (err) {
      setParamsError((err as Error).message);
    }
  }

  return (
    <div className="rounded-md border border-border bg-bg2 p-3">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-border bg-bg text-[11px] font-semibold text-text2">
          {index}
        </span>
        <span className="text-[10px] uppercase tracking-wide text-text2">
          {index === 0 ? 'primary' : `fallback ${index}`}
        </span>

        <select
          value={config.providerKey}
          onChange={(e) => onUpdate({ providerKey: e.target.value, params: {} })}
          className="ml-3 h-7 rounded-md border border-border bg-bg px-2 text-sm text-text outline-none focus:border-accent"
        >
          {providers.map((p) => (
            <option key={p.key} value={p.key}>
              {p.name} ({p.key})
            </option>
          ))}
        </select>

        <label className="ml-2 flex items-center gap-1 text-xs text-text2">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => onUpdate({ enabled: e.target.checked })}
          />
          включён
        </label>

        <div className="ml-auto inline-flex gap-1">
          <button
            type="button"
            disabled={isFirst}
            onClick={onMoveUp}
            className={btnGhostSm}
            title="Поднять"
          >
            ↑
          </button>
          <button
            type="button"
            disabled={isLast}
            onClick={onMoveDown}
            className={btnGhostSm}
            title="Опустить"
          >
            ↓
          </button>
          <button type="button" onClick={onRemove} className={btnGhostSm} title="Удалить">
            ✕
          </button>
        </div>
      </div>

      {hasParams && (
        <div className="mt-3">
          <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-text2">
            Параметры (JSON)
          </label>
          <textarea
            value={paramsDraft}
            onChange={(e) => setParamsDraft(e.target.value)}
            onBlur={commitParams}
            rows={Math.min(8, paramsDraft.split('\n').length + 1)}
            className="block w-full rounded-md border border-border bg-bg p-2 font-mono text-xs text-text outline-none focus:border-accent"
            placeholder="{}"
          />
          {paramsError && (
            <div className="mt-1 text-[11px] text-danger">JSON: {paramsError}</div>
          )}
          {provider?.paramsSchema && (
            <details className="mt-1 text-[10px] text-text-subtle">
              <summary className="cursor-pointer">Схема параметров</summary>
              <pre className="mt-1 overflow-x-auto rounded bg-bg3 p-2 font-mono">
                {JSON.stringify(provider.paramsSchema, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
